import { FetchAuthSessionOptions, IAuthService } from "../platform/services/auth/AuthService";
import {User} from "./typescript/interfaces";

export const getCurrentUser = async (authService: IAuthService): Promise<User | undefined> => {
    const user = await authService.getCurrentUser();
    return user || undefined;
}

export const getCurrentUserIdToken = async (authService: IAuthService, options?: FetchAuthSessionOptions) => {
    try {
        const { idToken } = (await authService.fetchAuthSession(options)).tokens ?? {};
        return idToken?.toString();
    } catch (error) {
        return undefined;
    }
}

/**
 * Decodes the `exp` claim of a JWT and returns it in milliseconds, or 0 if unavailable.
 */
export const getJwtExpMs = (jwt: string): number => {
    try {
        const [, payloadB64] = jwt.split('.') as [string, string, string?];
        if (!payloadB64 || typeof atob !== 'function') return 0;
        const jsonStr = atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'));
        const payload = JSON.parse(jsonStr);
        const exp = typeof payload.exp === 'number' ? payload.exp : 0;
        return exp * 1000;
    } catch {
        return 0;
    }
}

/**
 * Returns an id token guaranteed to be valid for at least `minTtlMs`,
 * force-refreshing the auth session if the cached token is about to expire.
 * Falls back to the cached token if the refresh fails.
 */
export const getFreshUserIdToken = async (authService: IAuthService, minTtlMs: number) => {
    const token = await getCurrentUserIdToken(authService);
    if (!token) return token;
    const expMs = getJwtExpMs(token);
    if (expMs && expMs - Date.now() < minTtlMs) {
        const fresh = await getCurrentUserIdToken(authService, { forceRefresh: true });
        return fresh ?? token;
    }
    return token;
}

// Legacy functions for backward compatibility - will be deprecated
// @deprecated Use authService.getCurrentUser() instead
export const getCurrentUserAmplify = async () => {
    throw new Error('getCurrentUserAmplify is deprecated. Use authService.getCurrentUser() instead.');
};
